import React, {FC, PropsWithChildren} from 'react';

import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DateCell} from '../../../../libs/shared/ui/src/components/tables/cells/dateCell';
import {HandleCell} from '../../../../libs/shared/ui/src/components/tables/cells/handleCell';
import {IconCell} from '../../../../libs/shared/ui/src/components/tables/cells/iconCell';
import {NumberCell} from '../../../../libs/shared/ui/src/components/tables/cells/numberCell';
import {TeammateCell} from '../../../../libs/shared/ui/src/components/tables/cells/teammateCell';
import {TextCell} from '../../../../libs/shared/ui/src/components/tables/cells/textCell';
import {VirtualTableColumnCellsEnum} from '../../../../libs/shared/ui/src/components/tables/helpers/tableHelpers';
import {TableColumn} from '../../../../libs/shared/ui/src/components/tables/tableColumn';
import {
  TableLoadingRowsContext,
  TableLoadingRowsContextProps,
} from '../../../../libs/shared/ui/src/components/tables/tableLoadingRowsContext';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {AccountCell} from './cells/accountCell';
import {AvatarCell} from './cells/avatarCell';
import {CardCell} from './cells/cardCell';
import {CardGroupCell} from './cells/cardGroupCell';
import {ChannelCell} from './cells/channelCell';
import {CircleCell} from './cells/circleCell';
import {CustomFieldCell} from './cells/customFieldCell';
import {InboxCell} from './cells/inboxCell';
import {NamespaceCell} from './cells/namespaceCell';
import {TagCell} from './cells/tagCell';
import {TeamPillsCell} from './cells/teamPillsCell';

const contextValue: TableLoadingRowsContextProps = {
  loadingCellRenderer,
};

export const TableLoadingRowsProvider: FC<PropsWithChildren> = ({children}) => (
  <TableLoadingRowsContext.Provider value={contextValue}>{children}</TableLoadingRowsContext.Provider>
);

function loadingCellRenderer(
  index: number,
  column: TableColumn<any>,
  shouldShowStickyGradient: boolean,
  size: VisualSizesEnum | undefined,
) {
  switch (column.cellType) {
    case VirtualTableColumnCellsEnum.ACCOUNT:
      return (
        <AccountCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.AVATAR:
      return (
        <AvatarCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.CARD:
      return (
        <CardCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.CUSTOM_FIELD:
      return (
        <CustomFieldCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.ICON:
      return (
        <IconCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          iconSize={column.settings?.loading?.iconSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.NAMESPACE:
      return (
        <NamespaceCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          value={globalNamespace}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.DATE:
      return (
        <DateCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.INBOX:
      return (
        <InboxCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.HANDLE:
      return (
        <HandleCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          hasChannel={false}
          onComposeMessage={() => {}}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.TEAMMATE:
      return (
        <TeammateCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.TEXT:
      return (
        <TextCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
        />
      );
    case VirtualTableColumnCellsEnum.NUMBER:
      return (
        <NumberCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
        />
      );
    case VirtualTableColumnCellsEnum.CARD_GROUP:
      return (
        <CardGroupCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
        />
      );
    case VirtualTableColumnCellsEnum.CHANNELS:
      return (
        <ChannelCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.CIRCLE:
      return (
        <CircleCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          avatarSize={column.settings?.loading?.avatarSize}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.TEAM_PILLS:
      return (
        <TeamPillsCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    case VirtualTableColumnCellsEnum.TAG:
      return (
        <TagCell
          key={`loading-cell-${index}`}
          isLoading
          size={size}
          settings={column.settings}
          index={index}
          shouldShowStickyGradient={shouldShowStickyGradient}
        />
      );
    default: {
      throw new UnreachableError(column.cellType);
    }
  }
}
